import { getStaffingColor } from "./utils";

const TENURE_KEY_ORDER = [
  "0_0_6_months",
  "0_6_1_year",
  "1_3_years",
  "3_5_years",
  "5_10_years",
  "10_15_years",
  "15_20_years",
  "over_20_years",
  "unknown",
];

const TENURE_KEY_COLORS = {
  "0_0_6_months": "#f4a6b8",
  "0_6_1_year": "#f5b8c8",
  "1_3_years": "#f5c78a",
  "3_5_years": "#f5e08a",
  "5_10_years": "#b8e0a8",
  "10_15_years": "#7ec87e",
  "15_20_years": "#8ec5e8",
  over_20_years: "#5a9fd4",
  unknown: "#d1d5db",
};

const DEPARTMENT_SHORT_CODES = {
  "Отделение Эскизного Проектирования": "ЭП",
  "РП ОЗ": "ОЗ",
  "РП МЖК": "МЖК",
  "АН и качество": "АН",
  "Административное отделение": "АУП",
  "Отделение построения": "Построение",
  "Финансовое отделение": "Финансы",
  "Отделение квалификации": "Квалификация",
  Производство: "Производство",
};

const extractDepartmentCode = (name) => {
  const numbered = name.match(/^\d+(?:\.\d+)?\s+(.+)$/);
  const label = numbered ? numbered[1] : name;
  return DEPARTMENT_SHORT_CODES[label] || label;
};

const getCountByKeys = (items, keys) =>
  items
    .filter((item) => keys.includes(item.key))
    .reduce((sum, item) => sum + item.count, 0);

const getStaffingBadge = (percent) => {
  if (percent == null) return null;
  const color = getStaffingColor(percent);
  if (color === "green") return { text: "Норма", tone: "green" };
  if (color === "orange") return { text: "Внимание", tone: "orange" };
  return { text: "Риск", tone: "red" };
};

const normalizeTurnoverStatus = (status) =>
  status === "риск" || status === "risk" ? "risk" : "normal";

const normalizePlan = (plan) =>
  plan != null && plan > 0 ? plan : null;

const buildTenureCategories = (departments) => {
  const labelByKey = new Map();

  departments.forEach((dept) => {
    (dept.tenure_distribution?.items || []).forEach((item) => {
      if (!labelByKey.has(item.key)) {
        labelByKey.set(item.key, item.label);
      }
    });
  });

  return TENURE_KEY_ORDER.filter((key) => labelByKey.has(key)).map((key) => ({
    key,
    label: labelByKey.get(key),
    color: TENURE_KEY_COLORS[key] || "#d1d5db",
  }));
};

const mapTenureDepartment = (dept) => {
  const items = (dept.tenure_distribution?.items || []).filter(
    (item) => item.count > 0,
  );
  const total = dept.tenure_distribution?.total || 0;

  return {
    id: dept.department_id,
    code: extractDepartmentCode(dept.department_name),
    name: dept.department_name,
    total,
    breakdown: Object.fromEntries(items.map((item) => [item.key, item.count])),
  };
};

const mapGenderDepartment = (dept) => {
  const distribution = dept.gender_distribution || {};
  const malePercent = distribution.male_percent ?? 0;
  const femalePercent = distribution.female_percent ?? 0;
  const unknownCount =
    distribution.items?.find((item) => item.key === "unknown")?.count || 0;
  const total = distribution.total || dept.employee_count || 0;
  const unknownPercent =
    total > 0
      ? Math.max(0, Math.round((unknownCount / total) * 1000) / 10)
      : Math.max(0, Math.round((100 - malePercent - femalePercent) * 10) / 10);

  return {
    id: dept.department_id,
    code: extractDepartmentCode(dept.department_name),
    name: dept.department_name,
    womenPercent: femalePercent,
    menPercent: malePercent,
    unknownPercent,
  };
};

const mapTurnoverDepartment = (dept) => {
  const turnover = dept.turnover || {};

  return {
    id: dept.department_id,
    code: extractDepartmentCode(dept.department_name),
    name: dept.department_name,
    resigned: turnover.dismissed_count ?? 0,
    turnoverPercent: turnover.percent ?? 0,
    status: normalizeTurnoverStatus(turnover.status),
    statusLabel: turnover.status === "риск" ? "Риск" : "Норма",
  };
};

const aggregateGender = (departments) => {
  const totals = { male: 0, female: 0, unknown: 0 };

  departments.forEach((dept) => {
    (dept.gender_distribution?.items || []).forEach((item) => {
      if (item.key === "male") totals.male += item.count;
      if (item.key === "female") totals.female += item.count;
      if (item.key === "unknown") totals.unknown += item.count;
    });
  });

  const total = totals.male + totals.female + totals.unknown;

  return {
    womenPercent:
      total > 0 ? Math.round((totals.female / total) * 1000) / 10 : 0,
    menPercent: total > 0 ? Math.round((totals.male / total) * 1000) / 10 : 0,
    unknownPercent:
      total > 0 ? Math.round((totals.unknown / total) * 1000) / 10 : 0,
    total,
    items: [
      totals.male > 0 && {
        key: "male",
        label: "мужчины",
        count: totals.male,
        percent: total > 0 ? Math.round((totals.male / total) * 1000) / 10 : 0,
      },
      totals.female > 0 && {
        key: "female",
        label: "женщины",
        count: totals.female,
        percent:
          total > 0 ? Math.round((totals.female / total) * 1000) / 10 : 0,
      },
      totals.unknown > 0 && {
        key: "unknown",
        label: "не указан",
        count: totals.unknown,
        percent:
          total > 0 ? Math.round((totals.unknown / total) * 1000) / 10 : 0,
      },
    ].filter(Boolean),
  };
};

const aggregateTenureHighlights = (departments) => {
  const items = departments.flatMap(
    (dept) => dept.tenure_distribution?.items || [],
  );
  const total = departments.reduce(
    (sum, dept) => sum + (dept.tenure_distribution?.total || 0),
    0,
  );

  if (!total) return [];

  return [
    {
      label: "Новички до 1 года",
      percent: Math.round(
        (getCountByKeys(items, ["0_0_6_months", "0_6_1_year"]) / total) * 100,
      ),
      tone: "pink",
    },
    {
      label: "Ядро 3–10 лет",
      percent: Math.round(
        (getCountByKeys(items, ["3_5_years", "5_10_years"]) / total) * 100,
      ),
      tone: "green",
    },
    {
      label: "Эксперты 10+ лет",
      percent: Math.round(
        (getCountByKeys(items, [
          "10_15_years",
          "15_20_years",
          "over_20_years",
        ]) /
          total) *
          100,
      ),
      tone: "blue",
    },
  ];
};

const mapDepartment = (dept) => {
  const plan = normalizePlan(dept.plan);
  const fact = dept.employee_count ?? dept.fact ?? 0;
  const deptStaffingPercent =
    dept.staffing_adequacy_percent != null
      ? Math.round(dept.staffing_adequacy_percent)
      : plan
        ? Math.round((fact / plan) * 100)
        : null;

  return {
    id: dept.department_id ?? dept.id,
    code: extractDepartmentCode(dept.department_name ?? dept.name),
    name: dept.department_name ?? dept.name,
    plan,
    fact,
    deficit: plan != null ? fact - plan : null,
    staffingPercent: deptStaffingPercent,
  };
};

const recalculateSummary = (departments, currentSummary) => {
  const withPlan = departments.filter((dept) => dept.plan != null);
  const plannedHeadcount = withPlan.length
    ? withPlan.reduce((sum, dept) => sum + dept.plan, 0)
    : null;
  const actualHeadcount = currentSummary.actualHeadcount;
  const staffingPercent =
    plannedHeadcount != null && plannedHeadcount > 0
      ? Math.round((actualHeadcount / plannedHeadcount) * 100)
      : null;
  const deficit =
    plannedHeadcount != null ? actualHeadcount - plannedHeadcount : null;

  return {
    ...currentSummary,
    plannedHeadcount:
      plannedHeadcount != null && plannedHeadcount > 0 ? plannedHeadcount : null,
    staffingPercent,
    deficit,
    staffingBadge: getStaffingBadge(staffingPercent),
    deficitBadge:
      deficit != null && deficit < 0
        ? { text: "Риск", tone: "red" }
        : null,
  };
};

const getActiveDepartments = (departments) =>
  (departments || []).filter((dept) => (dept.employee_count || 0) > 0);

export const mapStaffStatsToDashboard = (data) => {
  const rawDepartments = data.departments || [];
  const activeDepartments = getActiveDepartments(rawDepartments);
  const plannedHeadcount = normalizePlan(data.staffing_adequacy?.planned_headcount);
  const actualHeadcount =
    data.staffing_adequacy?.actual_headcount ?? data.active_users_count ?? 0;
  const staffingPercent =
    data.staffing_adequacy?.percent != null
      ? Math.round(data.staffing_adequacy.percent)
      : plannedHeadcount
        ? Math.round((actualHeadcount / plannedHeadcount) * 100)
        : null;
  const deficit =
    plannedHeadcount != null ? actualHeadcount - plannedHeadcount : null;
  const turnoverPercent = data.turnover_rate?.percent ?? 0;

  const summary = {
    plannedHeadcount,
    actualHeadcount,
    staffingPercent,
    deficit,
    turnoverPercent,
    turnoverAboveNorm: null,
    staffingBadge: getStaffingBadge(staffingPercent),
    deficitBadge:
      deficit != null && deficit < 0
        ? { text: "Риск", tone: "red" }
        : null,
  };

  const staffingByDepartment = rawDepartments.map(mapDepartment);
  const tenureCategories = buildTenureCategories(activeDepartments);
  const aggregatedGender = aggregateGender(activeDepartments);

  const turnoverItems = activeDepartments
    .map(mapTurnoverDepartment)
    .filter((item) => item.turnoverPercent != null)
    .sort((a, b) => b.turnoverPercent - a.turnoverPercent);

  return {
    summary,
    staffingByDepartment,
    turnover: {
      summary: {
        dismissedCount: data.turnover_rate?.dismissed_count ?? 0,
        averageHeadcount: data.turnover_rate?.average_headcount ?? 0,
        turnoverPercent,
        hiresCount: data.hires?.count ?? 0,
        transfersCount: data.transfers?.count ?? 0,
      },
      items: turnoverItems,
      dismissals: data.dismissals?.items || [],
      hires: data.hires?.items || [],
      transfers: data.transfers?.items || [],
    },
    tenure: {
      highlights: aggregateTenureHighlights(activeDepartments),
      categories: tenureCategories,
      departments: activeDepartments
        .map(mapTenureDepartment)
        .filter((dept) => dept.total > 0),
    },
    gender: {
      ...aggregatedGender,
      hasDepartmentBreakdown: true,
      departments: activeDepartments
        .map(mapGenderDepartment)
        .filter(
          (dept) =>
            dept.womenPercent != null ||
            dept.menPercent != null ||
            dept.unknownPercent > 0,
        ),
    },
    period: data.period,
  };
};

export const applyHeadcountPlansUpdate = (dashboardData, updatedDepartments) => {
  const updatedMap = new Map(
    updatedDepartments.map((dept) => [dept.department_id, mapDepartment(dept)]),
  );

  const staffingByDepartment = dashboardData.staffingByDepartment.map((dept) =>
    updatedMap.has(dept.id) ? updatedMap.get(dept.id) : dept,
  );

  return {
    ...dashboardData,
    staffingByDepartment,
    summary: recalculateSummary(staffingByDepartment, dashboardData.summary),
  };
};

export const getStaffingTotals = (departments, summary) => {
  const withPlan = departments.filter((dept) => dept.plan != null && dept.plan > 0);
  const plan = withPlan.reduce((sum, dept) => sum + dept.plan, 0);
  const fact = departments.reduce((sum, dept) => sum + dept.fact, 0);

  if (withPlan.length > 0) {
    return {
      plan,
      fact,
      deficit: fact - plan,
      staffingPercent: Math.round((fact / plan) * 100),
      hasPlan: true,
    };
  }

  return {
    plan: summary.plannedHeadcount,
    fact: summary.actualHeadcount,
    deficit: summary.deficit,
    staffingPercent: summary.staffingPercent,
    hasPlan: summary.plannedHeadcount != null,
  };
};
